/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import { View, Text } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { logger } from "../utils/logger";
import FullStory from "../utils/fullStory";
import { createApiClient, apiClient } from "../api/apiClient";
import {
  setLocations,
  setLocationWithAccess,
  setPeriodWeek,
  setRoles,
  setUserManagementRoles,
  setEmployees,
  setLoading,
  setError,
  setSelectedLocation,
  setFieldUsers,
  setCOEUsers,
  setLastPeriodWeekFetchDate,
  setNoAccessMode,
  setBasicAccessUser,
} from "../store/slices/appDataSlice";
import { setMasterData, setMasterDataError } from "../store/slices/masterDataSlice";

import { reportError, initializeUserContext } from "../utils/Instrumentation";
import LHLoader from "./LHLoader";
import AccessDenied from "./AccessDenied";
import { useMsal } from "./auth/MsalProvider";
import NoBasicAccess from "./NoBasicAccess";
import LimitedAccessPortal from "./LimitedAccessPortal";
import ActionPlanErrorModal from "../screens/ActionPlan/components/ActionPlanErrorModal";
import RackingInspectionErrorModal from '../screens/racking_inspection/components/RackingInspectionErrorModal';
import QualityAuditErrorModal from '../screens/qualityAudit/components/QualityAuditErrorModal';
import EmployeeUpdateErrorModal from '../screens/NewHireOnboarding/components/EmployeeUpdateErrorModal';
import ContactUsErrorModal from '../screens/ContactUs/components/ContactUsErrorModal';
import { RootState } from "../store";
import { TEXT } from "../constants/texts";
import { useLoginHistoryTracking } from "../hooks";

interface AppWrapperProps {
  children: React.ReactNode;
}

const AppWrapper: React.FC<AppWrapperProps> = ({ children }) => {
  useLoginHistoryTracking();

  const dispatch = useDispatch();
  const api = createApiClient();
  const { signOut } = useMsal();
  const [initComplete, setInitComplete] = useState(false);
  const [noLocationAccess, setNoLocationAccess] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string>("");
  // True when location-with-access errored or returned zero locations (as opposed to
  // returning real location data but the user's role being unauthorized).
  const [locationApiHadNoData, setLocationApiHadNoData] = useState(false);
  const lastIdentifiedUserIdRef = useRef<string | null>(null);

  const { username } = useSelector(
    (state: RootState) => state.auth.user || { username: "" }
  );
  // For mobile, use idToken from user as the accessToken
  const accessToken = useSelector((state: RootState) => (state.auth.user ? state.auth.token : undefined));
  const { error, lastPeriodWeekFetchDate, locations: appDataLocations, basicAccessUser } = useSelector((state: RootState) => state.appData);

  // Function to fetch employees based on location name
  const fetchEmployees = async (locationName: string) => {
    try {
      logger.log(`Fetching employees for location name: ${locationName}`);
      const employeesResponse = await apiClient(
        api,
        `/common/employees?location_name=${encodeURIComponent(locationName)}`,
        "GET"
      );
      dispatch(setEmployees(employeesResponse));
    } catch (error: any) {
      logger.error("Error fetching employees:", error);
      // Don't set global error as this might happen during navigation
      // and we don't want to show an error screen for this
      // Just log it and allow the app to continue with empty employees data
      dispatch(setEmployees([]));
    }
  };

  // Define initializeData function outside useEffect so it can be called from retry handler
  const initializeData = async () => {
    try {
      dispatch(setLoading(true));
      setNoLocationAccess(false);
      setLocationApiHadNoData(false);
      setAccessDeniedMessage("");
      dispatch(setNoAccessMode(false));

      // Prepare API calls array
      // Note: period-week API will be automatically called by the API interceptor if needed
      let locationResponse;
      let rolesResponse;
      // null = /roles call failed/returned nothing, so the allowlist check is skipped
      // (location-with-access is the authoritative source for roleName/roleId in that case)
      let allowedRolesFromApi: string[] | null = null;


      try {
        const apiCalls = [
          // API Call 1 - Get Location with Access
          apiClient(api, "/common/location-with-access", "POST"),
          // API Call 2 - Get Master Data (non-blocking: errors are handled separately)
          apiClient(api, "/common/master-data", "GET").catch((err: any) => {
            dispatch(setMasterDataError(err?.message ?? "Failed to load master data"));
            return null;
          }),
          // API Call 3 - Get allowed roles for access checks
          apiClient(api, "/roles", "GET").catch((err: any) => {
            logger.error("Error fetching /roles - skipping role allowlist check:", err);
            return null;
          }),

        ];

        // Run API calls in parallel with 15s timeout to prevent indefinite hang
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Access check timeout. Please check your network connection and try again.')), 15000)
        );
        const responses: any = await Promise.race([
          Promise.all(apiCalls),
          timeoutPromise
        ]);
        
        // Process responses
        locationResponse = responses[0];
                // Store master data in Redux (response[1] may be null if the call failed)
        const masterDataResponse = responses[1];
        if (masterDataResponse?.data) {
          dispatch(setMasterData(masterDataResponse.data));
        }

        // Build allowed role names from /roles response
        const rolesAccessResponse = responses[2];
        const allowedRolesFromResponse = Array.isArray(rolesAccessResponse?.data)
          ? rolesAccessResponse.data
              .map((role: any) => role?.roleName)
              .filter((roleName: any) => typeof roleName === "string" && roleName.trim().length > 0)
              .map((roleName: string) => roleName.trim())
          : [];
        if (allowedRolesFromResponse.length > 0) {
          allowedRolesFromApi = allowedRolesFromResponse;
        }


        // Store the raw /roles response so User Management can reuse it
        // instead of re-fetching the same endpoint via userManagementApi.getRoles
        if (Array.isArray(rolesAccessResponse?.data)) {
          dispatch(setUserManagementRoles(rolesAccessResponse.data));
        }

        // Fetch admin roles filtered by Observations availability + location details
        const firstLoc = locationResponse?.data?.locations?.[0];
        const roleParams: Record<string, any> = {
          page: 0, size: 9999, statuses: 'Enabled', availableIns: 'Observations',
        };
        if (firstLoc?.locationType) roleParams.locationTypes = firstLoc.locationType;
        if (firstLoc?.organizationType) roleParams.organizationTypes = firstLoc.organizationType;
        if (firstLoc?.is3pl !== null && firstLoc?.is3pl !== undefined) {
          roleParams.threePlStatuses = firstLoc.is3pl ? 'Yes' : 'No';
        }
        const rolesRaw = await api.get('/admin/role-management/roles', { params: roleParams });
        const rolesContent = Array.isArray(rolesRaw.data)
          ? rolesRaw.data
          : (rolesRaw.data?.content ?? []);
        rolesResponse = rolesContent.map((r: any) => ({
          ROLE_ID: r.id ?? r.name ?? '',
          ROLE_NAME: r.name ?? '',
        }));
      } catch (apiError: any) {
        // Handle API errors gracefully
        dispatch(setLoading(false));
        // DO NOT set initComplete yet — first determine which mode to show (basic access or denied)
        // to avoid rendering the wrong screen before noAccessMode is set
        
        const statusCode = apiError?.response?.status;
        logger.log("Location-with-access API error - Status:", statusCode, "Error:", apiError);
        
        // Only check basic access for 404 (user not found in location-with-access)
        // For other errors (500, network issues), show error without basic access check
        const shouldCheckBasicAccess = statusCode === 404 || !statusCode; // 404 or no status (network error)
        
        if (shouldCheckBasicAccess) {
          // User not found in location-with-access table (404), check basic access table
          try {
            logger.log("User not found in location-with-access (404), checking basic access...");
            const basicAccessResponse = await apiClient(
              api,
              "/admin/user-basic-access",
              "GET"
            );
            
            logger.log("Basic access response:", basicAccessResponse);
            
            // If user found in basic access table with status true, show ticketing portal
            if (basicAccessResponse && basicAccessResponse.status === true) {
              logger.log("✅ User has basic access - showing ticketing portal");
              dispatch(setBasicAccessUser(basicAccessResponse));
              setAccessDeniedMessage(""); // Clear error message for basic access users
              setNoLocationAccess(true);
              setLocationApiHadNoData(true);
              dispatch(setNoAccessMode(true));
              // NOW mark init as complete — after mode is determined
              setInitComplete(true);
              return;
            } else {
              logger.log("❌ User found in basic access but status is false");
            }
          } catch (basicAccessError: any) {
            // If basic access check fails (404 or other error), user has no access
            logger.log("❌ Basic access check failed - user not found:", basicAccessError?.response?.status);
          }
        }
        
        // Extract error message from the API response
        let errorMessage = '';
        if (apiError?.response?.data?.message) {
          errorMessage = apiError.response.data.message;
        } else if (apiError?.response?.data?.error) {
          errorMessage = apiError.response.data.error;
        } else if (typeof apiError?.response?.data === 'string') {
          errorMessage = apiError.response.data;
        } else if (apiError?.message) {
          errorMessage = apiError.message;
        } else {
          errorMessage = TEXT.ACCESS.DENIED_MESSAGES.API_ERROR;
        }
        
        // Show limited access ticketing portal (allows access request submission)
        setAccessDeniedMessage(errorMessage);
        setNoLocationAccess(true);
        dispatch(setNoAccessMode(true));
        // Mark init complete after mode is determined
        setInitComplete(true);
        
        // Log the error for debugging
        logger.error("API Error:", { statusCode, error: apiError, extractedMessage: errorMessage });
        reportError({
          severity: "ERROR",
          message: errorMessage,
          stackTrace: apiError instanceof Error ? apiError.stack : undefined,
        });
        
        return;
      }
      
      // Use the new setLocationWithAccess action for the new API response
      dispatch(setLocationWithAccess(locationResponse));
      dispatch(setRoles(rolesResponse));

      // Fetch basic-access record for ALL authenticated users — even full Navi users.
      // The `naviAccessAssignee` field from this response is used to auto-assign
      // Access Request tickets in SubmitTicketScreen (PUT after POST).
      try {
        const basicAccessResponse = await apiClient(api, "/admin/user-basic-access", "GET");
        if (basicAccessResponse) {
          dispatch(setBasicAccessUser(basicAccessResponse));
        }
      } catch {
        // Non-fatal — full Navi users without a basic-access record simply won't
        // have naviAccessAssignee available for auto-assign. Ticket still submits.
      }

      // Extract locations from the new API response structure
      const locationsArray = locationResponse?.data?.locations || [];
      
      // Fetch FieldUsers and COEUsers for the first location (or selectedLocation if available)
      const locationObj =
        locationsArray && locationsArray.length > 0
          ? selectedLocation || locationsArray[0]
          : null;

      // Turn off loader after initial APIs are complete
      dispatch(setLoading(false));
      setInitComplete(true);

      // Initialize AppDynamics user context after app data is loaded
      setTimeout(() => initializeUserContext(), 200);

      // Extract user info from the response
      const userInfo = locationResponse?.data?.userInfo;
      const roleName = userInfo?.roleName;
      const roleId = userInfo?.roleId;

      // Check if user has any assigned locations
      if (!locationsArray || locationsArray.length === 0) {
        // API succeeded but returned no locations - check basic access table
        try {
          logger.log("✋ No locations found in response, checking basic access...");
          const basicAccessResponse = await apiClient(
            api,
            "/admin/user-basic-access",
            "GET"
          );
          
          logger.log("Basic access response:", basicAccessResponse);
          
          // If user found in basic access table with status true, show ticketing portal
          if (basicAccessResponse && basicAccessResponse.status === true) {
            logger.log("✅ User has basic access - showing ticketing portal");
            dispatch(setBasicAccessUser(basicAccessResponse));
            setAccessDeniedMessage(""); // Clear error message for basic access users
            setNoLocationAccess(true);
            setLocationApiHadNoData(true);
            dispatch(setNoAccessMode(true));
            setInitComplete(true);
            return;
          } else {
            logger.log("❌ User found in basic access but status is false");
          }
        } catch (basicAccessError: any) {
          // If basic access check fails (404 or other error), user has no access
          logger.log("❌ Basic access check failed - user not found:", basicAccessError?.response?.status);
        }
        
        // User has no assigned locations and no basic access
        setAccessDeniedMessage(TEXT.ACCESS.DENIED_MESSAGES.NO_LOCATION);
        setNoLocationAccess(true);
        dispatch(setNoAccessMode(true));
        setInitComplete(true);
        return;
      }

      // Check if user has valid role information
      if (!roleName) {
        // User has no role information, redirect to limited ticketing portal
        setAccessDeniedMessage(TEXT.ACCESS.DENIED_MESSAGES.NO_ROLE);
        setNoLocationAccess(true);
        dispatch(setNoAccessMode(true));
        setInitComplete(true);
        return;
      }

      // Check if user's role is in the allowed roles list from /roles.
      // If /roles failed to load, skip this check — location-with-access already
      // confirmed a valid roleName/roleId, so don't block the user on a missing allowlist.
      const isAllowedRole = allowedRolesFromApi === null
        ? true
        : allowedRolesFromApi.some(
            (allowedRole) => allowedRole.toLowerCase() === String(roleName).toLowerCase()
          );
      if (!isAllowedRole || roleId === null || roleId === undefined) {
        // User's role is not authorized, redirect to limited ticketing portal
        setAccessDeniedMessage(TEXT.ACCESS.DENIED_MESSAGES.UNAUTHORIZED_ROLE(roleName));
        setNoLocationAccess(true);
        dispatch(setNoAccessMode(true));
        setInitComplete(true);
        return;
      }

      // For multiple locations, only set a default if no location is currently selected
      // This allows the persisted location from localStorage to take precedence
      if (
        locationsArray &&
        locationsArray.length > 1 &&
        !selectedLocation &&
        !persistedLocation
      ) {
        // Set the first location as default with proper formatting
        logger.log('Setting default location to first location:', locationsArray[0].name);
        const formattedLocation = {
          id: locationsArray[0].id || String(locationsArray[0].locationId), // Use string id from API, fallback to locationId
          displayText: locationsArray[0].name,
          name: locationsArray[0].name, // Keep the original name field
          locationId: locationsArray[0].locationId, // Keep the original locationId field
          ...locationsArray[0] // Keep all original properties
        };
        dispatch(setSelectedLocation(formattedLocation));
      } // Initial fetch of employees happens in the useEffect watching selectedLocation
      // User fetching (FieldUsers and COEUsers) also happens in the useEffect watching selectedLocation
      // We don't need to fetch employees or users here anymore as it will be triggered by the location change
    } catch (error: any) {
      reportError({
        severity: "ERROR",
        message: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
      dispatch(
        setError(
          error.message || "An error occurred while loading application data"
        )
      );
      dispatch(setLoading(false));
      setInitComplete(true);
    }
  };

  // Get the selected location from the Redux store
  const { selectedLocation } = useSelector((state: RootState) => state.appData);
  const { selectedLocation: persistedLocation } = useSelector(
    (state: RootState) => state.uiPreferences
  );

  // Effect to initialize app data
  useEffect(() => {
    if (Platform.OS === "web") {
      if (username) {
        initializeData();
      }
    } else {
      if (username && accessToken) {
        initializeData();
      }
    }
  }, Platform.OS === "web" ? [username] : [username, accessToken]);

  // Effect to fetch employees when selectedLocation changes
  useEffect(() => {
    if (selectedLocation && (selectedLocation.displayText || selectedLocation.name)) {
      const locationName = selectedLocation.displayText || selectedLocation.name;
      fetchEmployees(locationName);
      // Re-fetch roles with location-specific filters when location changes
      const fetchRolesForLocation = async () => {
        try {
          const locApi = createApiClient();
          const roleParams: Record<string, any> = {
            page: 0, size: 9999, statuses: 'Enabled', availableIns: 'Observations',
          };
          if (selectedLocation?.locationType) roleParams.locationTypes = selectedLocation.locationType;
          if (selectedLocation?.organizationType) roleParams.organizationTypes = selectedLocation.organizationType;
          if (selectedLocation?.is3pl !== null && selectedLocation?.is3pl !== undefined) {
            roleParams.threePlStatuses = selectedLocation.is3pl ? 'Yes' : 'No';
          }
          const rolesRaw = await locApi.get('/admin/role-management/roles', { params: roleParams });
          const rolesContent = Array.isArray(rolesRaw.data)
            ? rolesRaw.data
            : (rolesRaw.data?.content ?? []);
          const normalised = rolesContent.map((r: any) => ({
            ROLE_ID: r.id ?? r.name ?? '',
            ROLE_NAME: r.name ?? '',
          }));
          dispatch(setRoles(normalised));
        } catch (err) {
          logger.error('Error fetching roles for location:', err);
        }
      };
      fetchRolesForLocation();

      // Fetch FieldUsers and COEUsers for new location
      const fetchUsers = async () => {
        try {
          const locationName =
            selectedLocation.displayText || selectedLocation.name;
          const userResp = await apiClient(
            api,
            `/common/users/users-by-role-ids?roleIds=1,2,3,4,5,6,7,8,9&location=${encodeURIComponent(
              locationName
            )}`,
            "GET"
          );
          const users = userResp?.users || [];
          const COEUsers = users.filter((u: any) => u.Role === "COE" || u.Role === "Super User" || u.Role === "Region Leader/Division");
          const COEUserEmails = new Set(COEUsers.map((u: any) => u.UserEmail));
          
          // Filter to include all users except COE, Super User, and Region Leader/Division
          const FieldUsers = users.filter((u: any) => 
            !COEUserEmails.has(u.UserEmail)
          );
          
          dispatch(setFieldUsers(FieldUsers));
          dispatch(setCOEUsers(COEUsers));
        } catch {
          dispatch(setFieldUsers([]));
          dispatch(setCOEUsers([]));
        }
      };
      fetchUsers();
      // AppDynamics user context will be automatically updated via Redux store listener
    }
  }, [selectedLocation?.id]); // Only run when the location ID changes

  // Effect to sync appData selectedLocation with persisted location from uiPreferences
  useEffect(() => {
    if (persistedLocation && !selectedLocation) {
      // If we have a persisted location but no current selection in appData, sync it
      // Enrich with full data from appData.locations to ensure all fields are populated
      const fullLoc = appDataLocations?.find(
        l => l.id === persistedLocation.id || l.name === persistedLocation.displayText
      );
      dispatch(setSelectedLocation(
        fullLoc ? { ...fullLoc, displayText: fullLoc.name } : persistedLocation
      ));
    }
  }, [persistedLocation, selectedLocation, appDataLocations, dispatch]);

  // Identify user with FullStory when userInfo is available
  const userInfo = useSelector((state: RootState) => state.appData.userInfo);
  useEffect(() => {
    const identifyUser = async () => {
      try {
        if (!userInfo) {
          logger.debug('[FullStory] Waiting for userInfo data...');
          return;
        }
        
        // Use GPID as the unique identifier (uid) per FullStory best practices
        // GPID is opaque, non-guessable, and uniquely identifies a user
        const userId = userInfo.GPID?.trim();
        if (!userId) {
          logger.debug('[FullStory] Skipping identify - no valid GPID available');
          return;
        }

        // Avoid re-identifying the same user repeatedly (prevents session churn)
        if (lastIdentifiedUserIdRef.current === userId) {
          logger.debug('[FullStory] Skipping identify - user already identified in this session', { userId });
          return;
        }
        lastIdentifiedUserIdRef.current = userId;
        
        // Build properties object with only valid, non-empty values
        const userProperties: Record<string, string> = {};
        
        // Add email if available
        const email = (userInfo as any).email?.trim?.() || (userInfo as any).Email?.trim?.();
        if (email) userProperties.email = email;

        // FullStory displays the user name from `displayName`
        // Prefer a formatted email prefix; fallback to username.
        const nameSource = email || username || '';
        if (nameSource) {
          const prefix = nameSource.includes('@') ? nameSource.split('@')[0] : nameSource;
          const formattedName = prefix
            .split(/[._-]+/)
            .filter(Boolean)
            .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');
          if (formattedName) userProperties.displayName = formattedName;
        }
        
        logger.log('[FullStory] Identifying user with GPID', { userId, properties: userProperties });
        
        // Call FS.identify with GPID as userId and email/displayName as properties
        await FullStory.identify(userId, userProperties);

        logger.log('[FullStory] User successfully identified', { userId, properties: userProperties });
      } catch (error) {
        logger.error('[FullStory] Failed to identify user', error);
      }
    };
    
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    identifyUser();
  }, [userInfo]);

  // Function to retry initialization
  const handleRetry = () => {
    setInitComplete(false);
    if (username) {
      initializeData();
    }
  };

  if (!initComplete) {
    return <LHLoader testID="loading-indicator" />;
  }

  if (error) {
    return (
      <View
        testID="error-container"
        style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
      >
        <Text
          testID="error-message"
          style={{ color: "red", textAlign: "center" }}
        >
          {error}
        </Text>
      </View>
    );
  }

  if (noLocationAccess) {
    // Show the limited ticketing portal if either:
    // - location-with-access returned no usable data at all (error or empty locations), or
    // - the basic-access request is still pending (no team assigned yet)
    const isBasicAccessPending = basicAccessUser && basicAccessUser.status === true && (locationApiHadNoData || !basicAccessUser.team);

    if (isBasicAccessPending) {
      logger.log("Rendering LimitedAccessPortal for basic access user");
      return <LimitedAccessPortal />;
    }
    
    // Otherwise, both APIs failed (404) - show strict access denied (No Basic Access)
    logger.log("Rendering NoBasicAccess - user not found in either table");
    return <NoBasicAccess message={accessDeniedMessage} onRetry={handleRetry} onLogout={signOut} />;
  }

  return <>
    <ActionPlanErrorModal />
    <RackingInspectionErrorModal />
    <QualityAuditErrorModal />
    <EmployeeUpdateErrorModal />
    <ContactUsErrorModal />
    {children}
  </>;
};

export default AppWrapper;
